import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PlatformCatalogueEntry } from "@tdelivr/api-client/generated/event-platform/event-catalog.js";
import type { PlatformDeadLetterSummary } from "@tdelivr/api-client/generated/event-platform/dead-letter-management.js";

import { boundedNote } from "../board.ts";
import {
  DEAD_LETTERS_SCOPE,
  EVENT_CATALOGUE_SCOPE,
  deadLetterRow,
  listingNote,
  statesPresent,
  stateOf,
} from "../event-catalogue.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `dlq` in `16 WP15 Platform Machinery.dc.html`: the event catalogue and the dead letters.
 *
 * ```
 * dlq:['Event catalogue and dead letters','EVT-02 · EVT-03',
 *   'Events that couldn\'t be delivered wait here with their reason, so none are silently lost.',
 *   [['Dead letters','7','failed','unprocessed'],['Oldest','3 h','caution','age'],
 *    ['Redriven today','41','done','delivered'],['Poison','1','failed','fails every time']],
 *   ['Event','Consumer','Reason'],
 *   [['evt_88K2 payments.settled','finance-ledger','schema v2 not accepted','failed','dead-lettered'],
 *    ['evt_91AA delivery.exception.opened','notifications','provider timeout','caution','retrying 2 of 5'],
 *    ['evt_77Q0 trip.state.changed','search-index','poison message','failed','needs a human']],
 *   'warn','Redrive is idempotent: the consumer ignores events it has already processed.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                      | source                                                      |
 * | ------------------------------------------ | ----------------------------------------------------------- |
 * | the rows                                   | `EVT-03 GET /v1/platform/dead-letters` (ADR 0059)           |
 * | `Event` — `evt_88K2 payments.settled`      | `deadLetters.deadLetterId` and `.topic`. Q-196 on the width |
 * | `Consumer` — `finance-ledger`              | **nothing** — no service models a consumer. Q-198           |
 * | `Reason` — `schema v2 not accepted`        | `deadLetters.failureCode`, in the design's sentence case    |
 * | `failed · dead-lettered`, `caution · retrying`, `human · needs a human` | `deadLetters.status` |
 * | `retrying` **2 of 5**                      | **nothing** — nothing counts attempts. Q-199                |
 * | the event's owner and lifecycle            | `EVT-02 GET /v1/platform/event-catalog` (ADR 0058)          |
 * | the four KPI tiles                         | **nothing** — R-04 and Q-178; three of them are Q-199       |
 * | a primary command                          | **the design draws none.** `CREATE_REFUSAL`, Q-217          |
 *
 * ## Two listings on one page, and the design is why that is one grid
 *
 * The source chip names two services and both answer something, which is true of no other page of
 * this console. **The design still draws one grid, and every column and tile in it is EVT-03's.**
 * So the rows are the dead letters and EVT-02's catalogue fills the detail panel's five lines about
 * the event each one belongs to — the owner, the lifecycle, the schema subject, the visibility. A
 * second grid would be a section `CLAUDE.md` section 1 forbids adding, and `../event-catalogue.ts`
 * argues the composition and the name match it rests on, which is this board's own third gap
 * (**Q-217**) rather than one it inherited.
 *
 * ## Both scopes are required and only one listing is load-bearing
 *
 * Both are asked for before either call, so a missing grant names both scopes in one actionable
 * sentence. After that the two listings are not equal and are not treated as equal:
 *
 *   - **EVT-03 is the grid.** A failure there is the board's failure state; there is nothing to draw.
 *   - **EVT-02 is five lines of a panel.** A failure there degrades those five lines to the design's
 *     own `—` and keeps every dead letter on screen, with a sentence under the grid saying the
 *     catalogue did not answer. The alternative — one `Promise.all` and one error state — would hide
 *     every dead letter on the platform because a second service was unreachable, on the one page an
 *     operator opens *because* something is failing.
 *
 * ## What a platform-scoped read did not give this page
 *
 * Both halves of the gap were route work and both are done; what remains is not route work in either
 * service. `Consumer` is a third of the grid and no aggregate on this platform models a dead
 * letter's consumer (Q-198). The attempt counter behind `retrying 2 of 5` is three of the four tiles
 * and one badge, and retry execution is a declared non-responsibility of EVT-03, so the counter
 * belongs on whoever re-delivers (Q-199). ADR 0059's own consequence says this page "is drawn with
 * one of its three columns absent and three of its four tiles absent" — which is what this is, and
 * the measured verdict is the point.
 */

const eventCatalog = services.event_platform__event_catalog;
const deadLetters = services.event_platform__dead_letter_management;

export interface EventCatalogueProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Machinery › Event catalogue and dead letters`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Event catalogue and dead letters";
/**
 * The design's own source chip, word for word: `EVT-02 · EVT-03`.
 *
 * Both answer, and the order is the design's — the catalogue first, although the dead letters are
 * the rows. `navigation.ts` lists the same two owners in the same order.
 */
const SOURCE = "EVT-02 · EVT-03";
const DESCRIPTION =
  "Events that couldn't be delivered wait here with their reason, so none are silently lost.";
const COLUMNS = ["Event", "Consumer", "Reason"] as const;
const NOTE = "Redrive is idempotent: the consumer ignores events it has already processed.";
/** The design's own `noteK` for this page is `warn`, which its `NK` map draws in the amber tint. */
const NOTE_KIND = "warn" as const;
/*
 * There is no button constant here any more, and the reason is worth one note.
 *
 * `FORMS` has no `dlq` entry, so the design's heading button is `display:none` and its panel's
 * `Edit` is too. This comment used to explain the word on a button drawn anyway, because
 * `RecordsBoard` required a `primary` and drew it unconditionally — and it said so as a constraint
 * that could not be helped. It could: `primary` is optional now and `RowDetail`'s `Edit` follows
 * it, so this board passes neither and draws neither, which is what the design does.
 *
 * Left as a note rather than deleted because the comment outlived the thing it described by one
 * commit, which is the failure mode this repository keeps finding — prose asserting a constraint
 * that the diff beside it removed. Review caught it, not a check.
 */

/**
 * The most rows one request answers, which is both services' own maximum.
 *
 * One request per listing, and the catalogue's is asked for at the same bound rather than a smaller
 * one on the argument that it only fills a panel: a catalogue page cut short would leave rows whose
 * event *is* documented drawing `—`, which reads as "the platform documents no such event". When
 * either answer is truncated the board says so — `boundedNote` for the rows, `listingNote` for how
 * many of them the catalogue could name.
 */
const PER_FETCH = 200;

export function EventCatalogue({ transport, session, crumbs }: EventCatalogueProps): ReactNode {
  const [rows, setRows] = useState<readonly PlatformDeadLetterSummary[] | undefined>(undefined);
  const [entries, setEntries] = useState<readonly PlatformCatalogueEntry[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [catalogueTruncated, setCatalogueTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setEntries(undefined);
    setFailure(undefined);

    /*
     * Both scopes asked for before either call, not discovered from a refusal.
     *
     * One state naming both, because that is what an operator can act on: they can ask their
     * administrator for `dead-letter:platform:read` and `event-catalog:platform:read` by name. A 403
     * from either service renders the same state, so this is not the only guard; it is the one that
     * makes both scopes literals in this application's source, which is what `channel-scopes.mjs`
     * derives the Keycloak grant from. ADR 0049 §3, and the notification bell's three-console 403 is
     * what happens without it.
     */
    const missing = [DEAD_LETTERS_SCOPE, EVENT_CATALOGUE_SCOPE].filter(
      (scope) => !session.scopes.has(scope),
    );
    if (missing.length > 0) {
      setFailure({ kind: "forbidden", missingScopes: missing });
      return;
    }

    /*
     * `listPlatformDeadLetters` and `listPlatformEventCatalogue` — the platform operations, not the
     * tenant-bounded ones.
     *
     * EVT-03's seven others are all `/internal/v1` with a `tenantId` on the path, so there is no
     * wrong one to call there. EVT-02's `searchEventCatalog` is the trap: it is on `/internal/v1`,
     * it is bounded `(visibility='global' OR tenant_id=$1)` from the verified token, and it answers
     * the whole eighteen-field entry including every operator's prose. ADR 0058 published the two
     * side by side and left the first byte-for-byte as it was.
     *
     * Started together and awaited apart, which is the asymmetry this file's header argues: the dead
     * letters are the grid and the catalogue is five lines of a panel.
     */
    const deadLetterPage = deadLetters
      .createDeadLetterManagementClient(transport)
      .listPlatformDeadLetters(String(PER_FETCH));
    const cataloguePage = eventCatalog
      .createEventCatalogClient(transport)
      .listPlatformEventCatalogue(String(PER_FETCH));

    /*
     * The catalogue's rejection is caught here and not left for the `await` below, because an
     * unhandled rejection on a promise started before an earlier `await` threw is an unhandled
     * rejection warning rather than a handled failure.
     */
    const catalogue = cataloguePage.then(
      (page) => page,
      () => undefined,
    );

    try {
      const page = await deadLetterPage;
      setRows(page.deadLetters);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [DEAD_LETTERS_SCOPE]));
      return;
    }

    const documented = await catalogue;
    setEntries(documented?.entries);
    setCatalogueTruncated(documented !== undefined && documented.nextAfter !== null);
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const catalogueEntries = entries ?? [];
  const boardRows =
    rows === undefined
      ? undefined
      : rows
          .filter((summary) => state === "" || stateOf(summary).label === state)
          .map((summary) => deadLetterRow(summary, catalogueEntries, entries !== undefined));

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "dead letters",
    "ordered by tenant, then topic, then dead letter",
  );
  const span = listingNote(rows, entries);
  const spanWithCatalogueBound =
    span === undefined
      ? undefined
      : catalogueTruncated
        ? `${span} More than ${String(catalogueEntries.length)} catalogue entries exist, so a row drawing — may name an event documented on a later page.`
        : span;

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No event has been dead-lettered on any tenant. A row appears here when a consumer fails a message and the broker hands it to EVT-03."
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "warning", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(spanWithCatalogueBound === undefined ? {} : { footnote: spanWithCatalogueBound })}
      exportName="event-catalogue-dead-letters"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
